/**
 * The command input — the player's half of the conversation (architecture.md
 * §9; R7, rebuilt for D79).
 *
 * @remarks
 * Pure and prop-driven: a controlled input plus the QUESTION SURFACE — while
 * a question is open its text sits at the input line with any candidate
 * chips and a dismiss control (D79: a question is a state the player can
 * see). A chip SUBMITS its candidate's answer — it does not type for the
 * player (§8.7); what a click means is the app's callback, as ever. The
 * component owns only the form semantics and the accessible names.
 *
 * @packageDocumentation
 */

import type { CommandSuggestion } from '@orpheus/api-contracts';

import { type FormEvent, type KeyboardEvent, useEffect, useRef } from 'react';

/** One suggestion chip: its identity, what it shows, and the ANSWER it submits. */
/** One chip: the contract's shape (D156), re-exported for the shell. */
export type { CommandSuggestion };

/**
 * The directive field's DOM id — the one handle the shell may take on it
 * from outside (to return focus after a reference dialog closes), so the
 * string lives once, here, beside the element it names.
 */
export const COMMAND_INPUT_FIELD_ID = 'command-input-field';

/** Props for {@link CommandInputComponent}. */
export interface CommandInputComponentProps {
	/** Whether the input accepts submissions (the session must be ready). */
	readonly isEnabled: boolean;
	/** ArrowDown — browse history toward the newest entry. */
	readonly onHistoryDown: () => void;
	/** ArrowUp — browse history toward the oldest entry. */
	readonly onHistoryUp: () => void;
	/** The controlled-input change callback. */
	readonly onInputValueChange: (nextValue: string) => void;
	/** Submit the current value (Enter or the submit button). */
	readonly onSubmit: () => void;
	/** Hide the open question (present only while one is open — renders the dismiss control). */
	readonly onDismissQuestion?: () => void;
	/** A suggestion chip was chosen — submit its answer. */
	readonly onSuggestionSelected: (suggestion: CommandSuggestion) => void;
	/** The question the chips answer, when one is parked (rendered above them). */
	readonly promptQuestion: string | undefined;
	/** The chips to offer (empty = none rendered). */
	readonly suggestions: readonly CommandSuggestion[];
	/** The controlled input value. */
	readonly value: string;
}

/**
 * Render the command line: chips (when a question is parked), the input, and
 * its submit control.
 *
 * @param props - {@link CommandInputComponentProps}
 * @returns The rendered command line
 */
export const CommandInputComponent = ({
	isEnabled,
	onDismissQuestion,
	onHistoryDown,
	onHistoryUp,
	onInputValueChange,
	onSubmit,
	onSuggestionSelected,
	promptQuestion,
	suggestions,
	value,
}: CommandInputComponentProps) => {
	/*
	 * D117 (Mike): the directive line takes focus when the session mounts —
	 * BEGIN DESCENT lands the player mid-sentence, ready to type. Imperative
	 * rather than the `autoFocus` attribute deliberately: this is a
	 * post-gesture SPA mount answering an action the player just took, and
	 * stating it as an effect keeps that intent (and its one-shot scope)
	 * explicit where the attribute would look like page-load focus stealing.
	 */
	const inputElementRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (isEnabled) {
			inputElementRef.current?.focus();
		}
		// Mount-only by design: later `isEnabled` flips must not yank focus
		// from wherever the player has taken it.
		// eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on mount (see above)
	}, []);

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		onSubmit();
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'ArrowUp') {
			event.preventDefault();
			onHistoryUp();
		} else if (event.key === 'ArrowDown') {
			event.preventDefault();
			onHistoryDown();
		}
	};

	return (
		<form className="command-input" onSubmit={handleSubmit}>
			{suggestions.length > 0 || promptQuestion !== undefined ? (
				<div className="command-input__suggestions" role="group" aria-label="Open question">
					{promptQuestion === undefined ? undefined : (
						<span className="command-input__prompt-question">{promptQuestion}</span>
					)}
					{suggestions.map((suggestion) => (
						<button
							// D179: a chip that does something irreversible wears the danger color — the same promise BEGIN AGAIN makes.
							className={suggestion.tone === 'danger' ? 'command-input__suggestion command-input__suggestion--danger' : 'command-input__suggestion'}
							key={suggestion.suggestionKey}
							onClick={() => {
								onSuggestionSelected(suggestion);
							}}
							type="button"
						>
							{suggestion.label}
						</button>
					))}
					{onDismissQuestion === undefined ? undefined : (
						<button
							aria-label="Dismiss question"
							className="command-input__dismiss-question"
							onClick={() => {
								onDismissQuestion();
							}}
							type="button"
						>
							✕
						</button>
					)}
				</div>
			) : undefined}
			<div className="command-input__line">
				<label className="command-input__label" htmlFor={COMMAND_INPUT_FIELD_ID}>
					Directive
				</label>
				{/* D85 addendum (Mike's ruling, Calder's §3.2): the glyph's one
				    grammar — ▷ immediately precedes the player's words — now holds
				    at the point of entry as well as at the head of every turn it
				    becomes. Right-hand side only ("▷ DIRECTIVE" would be a bullet
				    on a label), and a SIBLING of the label rather than inside it:
				    the sigil is the prompt, not part of the field's name, so the
				    accessible name stays "Directive" (aria-hidden by the same D85
				    principle — the reader already knows where the field is). */}
				<span aria-hidden="true" className="command-input__prompt-sigil">
					▷
				</span>
				<input
					autoComplete="off"
					className="command-input__field"
					disabled={!isEnabled}
					id={COMMAND_INPUT_FIELD_ID}
					ref={inputElementRef}
					onChange={(event) => {
						onInputValueChange(event.target.value);
					}}
					onKeyDown={handleKeyDown}
					spellCheck={false}
					type="text"
					value={value}
				/>
				<button className="command-input__submit" disabled={!isEnabled} type="submit">
					Send
				</button>
			</div>
		</form>
	);
};
